/** @format */

import React, { useMemo, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    MenuItem,
    Slider,
    FormControlLabel,
    Switch,
    CircularProgress,
    Chip,
    Divider,
} from "@mui/material";
import { ScienceOutlined, SaveOutlined, RestartAlt } from "@mui/icons-material";

import {
    CollectSectionCard,
    CollectSectionHeader,
    CollectSectionTitle,
    CollectSectionSubtitle,
    CollectPrimaryButton,
    CollectGhostButton,
} from "../styles";
import { colors } from "../../../utility/types/constant";
import {
    IPredictionAnalysis,
    IScenarioInputs,
    ISimulationResult,
    PredictionRiskLevel,
    PREDICTION_RISK_LEVEL_LABELS,
    RECOVERY_ACTION_LABELS,
} from "../../../utility/types/collectTypes";
import {
    simulatePrediction,
    addScenario,
} from "../../../redux/services/predictionService";
import { useToast } from "../../../providers/ToastProvider";

// ─────────────────────────────────────────────────────────────────────────
// WhatIfSimulator (Stage 2J) — owner/admin tool to adjust assumptions and
// preview the impact on the prediction WITHOUT touching the baseline (AC6).
// Previews are server-computed (no persistence); saving stores a named
// scenario alongside the prediction.
// ─────────────────────────────────────────────────────────────────────────

interface Props {
    prediction: IPredictionAnalysis;
    canMutate: boolean;
    onScenarioSaved: (updated: IPredictionAnalysis) => void;
}

const FONT = "'Montserrat', sans-serif";

const RISK_COLORS: Record<PredictionRiskLevel, string> = {
    low: "#16a34a",
    medium: "#f59e0b",
    high: "#ea580c",
    critical: "#dc2626",
};

function fmtCurrency(amount: number, currency = "USD"): string {
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
            maximumFractionDigits: 0,
        }).format(amount);
    } catch {
        return `$${Math.round(amount)}`;
    }
}

function pct(value: number): string {
    return `${Math.round((value ?? 0) * 100)}%`;
}

function numOrUndef(v: string): number | undefined {
    if (v === "") return undefined;
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
}

const WhatIfSimulator: React.FC<Props> = ({
    prediction,
    canMutate,
    onScenarioSaved,
}) => {
    const toast = useToast();
    const [inputs, setInputs] = useState<IScenarioInputs>({});
    const [result, setResult] = useState<ISimulationResult | null>(null);
    const [simulating, setSimulating] = useState(false);
    const [saving, setSaving] = useState(false);
    const [scenarioName, setScenarioName] = useState("");

    const currency = useMemo(() => {
        const tl = prediction.chartData?.find(
            (c) => c.type === "receivables_timeline",
        );
        return (tl?.data?.currency as string) || "USD";
    }, [prediction]);

    const hasInputs = useMemo(
        () => Object.values(inputs).some((v) => v !== undefined && v !== ""),
        [inputs],
    );

    const setField = <K extends keyof IScenarioInputs>(
        key: K,
        value: IScenarioInputs[K],
    ) => {
        setInputs((prev) => {
            const next = { ...prev };
            if (value === undefined || value === ("" as any)) {
                delete next[key];
            } else {
                next[key] = value;
            }
            return next;
        });
    };

    const handlePreview = async () => {
        setSimulating(true);
        try {
            const res = await simulatePrediction(
                prediction.businessId,
                prediction._id,
                inputs,
            );
            setResult(res);
        } catch (err: any) {
            toast.error(err.message || "Could not run preview");
        } finally {
            setSimulating(false);
        }
    };

    const handleReset = () => {
        setInputs({});
        setResult(null);
        setScenarioName("");
    };

    const handleSave = async () => {
        if (!hasInputs) {
            toast.error("Adjust at least one assumption before saving.");
            return;
        }
        setSaving(true);
        try {
            const updated = await addScenario(
                prediction.businessId,
                prediction._id,
                inputs,
                scenarioName.trim() || undefined,
            );
            toast.success("Scenario saved.");
            onScenarioSaved(updated);
            handleReset();
        } catch (err: any) {
            toast.error(err.message || "Could not save scenario");
        } finally {
            setSaving(false);
        }
    };

    const baselineCash = prediction.projectedCashImpact;
    const baselineOnTime = prediction.onTimeProbability;

    return (
        <CollectSectionCard>
            <CollectSectionHeader>
                <Box>
                    <CollectSectionTitle>What-if simulator</CollectSectionTitle>
                    <CollectSectionSubtitle>
                        Adjust assumptions to see how they could change the outlook.
                        The original prediction is never overwritten.
                    </CollectSectionSubtitle>
                </Box>
            </CollectSectionHeader>

            {!canMutate ? (
                <Typography
                    sx={{
                        fontFamily: FONT,
                        fontSize: "0.85rem",
                        color: "#6b7280",
                        py: 1,
                    }}
                >
                    Only owners and admins can run what-if scenarios.
                </Typography>
            ) : (
                <>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                            gap: 2,
                        }}
                    >
                        <TextField
                            size="small"
                            type="number"
                            label="Invoice amount"
                            value={inputs.invoiceAmount ?? ""}
                            onChange={(e) =>
                                setField("invoiceAmount", numOrUndef(e.target.value))
                            }
                        />
                        <TextField
                            size="small"
                            type="number"
                            label="Payment terms (days)"
                            value={inputs.paymentTermsDays ?? ""}
                            onChange={(e) =>
                                setField("paymentTermsDays", numOrUndef(e.target.value))
                            }
                        />
                        <TextField
                            size="small"
                            type="number"
                            label="Extra expected delay (days)"
                            value={inputs.expectedPaymentDelayDays ?? ""}
                            onChange={(e) =>
                                setField(
                                    "expectedPaymentDelayDays",
                                    numOrUndef(e.target.value),
                                )
                            }
                        />
                        <TextField
                            size="small"
                            type="number"
                            label="Reminder lead (days before due)"
                            value={inputs.reminderLeadDays ?? ""}
                            onChange={(e) =>
                                setField("reminderLeadDays", numOrUndef(e.target.value))
                            }
                        />
                        <TextField
                            size="small"
                            select
                            label="Manual risk nudge"
                            value={
                                inputs.customerRiskAdjustment === undefined
                                    ? ""
                                    : String(inputs.customerRiskAdjustment)
                            }
                            onChange={(e) =>
                                setField(
                                    "customerRiskAdjustment",
                                    e.target.value === ""
                                        ? undefined
                                        : Number(e.target.value),
                                )
                            }
                        >
                            <MenuItem value="">No change</MenuItem>
                            <MenuItem value="-2">Much better</MenuItem>
                            <MenuItem value="-1">Better</MenuItem>
                            <MenuItem value="1">Worse</MenuItem>
                            <MenuItem value="2">Much worse</MenuItem>
                        </TextField>
                        <TextField
                            size="small"
                            type="number"
                            label="Expected partial payment"
                            value={inputs.expectedPartialPaymentAmount ?? ""}
                            onChange={(e) =>
                                setField(
                                    "expectedPartialPaymentAmount",
                                    numOrUndef(e.target.value),
                                )
                            }
                        />
                    </Box>

                    <Box sx={{ mt: 2 }}>
                        <Typography
                            sx={{
                                fontFamily: FONT,
                                fontSize: "0.8rem",
                                fontWeight: 600,
                                color: "#6b7280",
                                mb: 1,
                            }}
                        >
                            Recovery intensity:{" "}
                            {inputs.recoveryIntensity ?? "—"}
                        </Typography>
                        <Slider
                            value={inputs.recoveryIntensity ?? 0}
                            min={0}
                            max={100}
                            step={10}
                            valueLabelDisplay="auto"
                            onChange={(_, v) =>
                                setField("recoveryIntensity", v as number)
                            }
                            sx={{ color: colors.mainSecondary, maxWidth: 360 }}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            mt: 1,
                        }}
                    >
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!inputs.smsEnabled}
                                    onChange={(e) =>
                                        setField(
                                            "smsEnabled",
                                            e.target.checked || undefined,
                                        )
                                    }
                                />
                            }
                            label="SMS"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!inputs.emailEnabled}
                                    onChange={(e) =>
                                        setField(
                                            "emailEnabled",
                                            e.target.checked || undefined,
                                        )
                                    }
                                />
                            }
                            label="Email"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!inputs.applyLateFee}
                                    onChange={(e) =>
                                        setField(
                                            "applyLateFee",
                                            e.target.checked || undefined,
                                        )
                                    }
                                />
                            }
                            label="Late fee"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!inputs.markDisputed}
                                    onChange={(e) =>
                                        setField(
                                            "markDisputed",
                                            e.target.checked || undefined,
                                        )
                                    }
                                />
                            }
                            label="Disputed"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!inputs.markNonResponsive}
                                    onChange={(e) =>
                                        setField(
                                            "markNonResponsive",
                                            e.target.checked || undefined,
                                        )
                                    }
                                />
                            }
                            label="Non-responsive"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!inputs.excludeBrokenPromises}
                                    onChange={(e) =>
                                        setField(
                                            "excludeBrokenPromises",
                                            e.target.checked || undefined,
                                        )
                                    }
                                />
                            }
                            label="Ignore broken promises"
                        />
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            gap: 1.5,
                            mt: 2,
                            flexWrap: "wrap",
                        }}
                    >
                        <CollectPrimaryButton
                            startIcon={
                                simulating ? (
                                    <CircularProgress size={16} color="inherit" />
                                ) : (
                                    <ScienceOutlined />
                                )
                            }
                            onClick={handlePreview}
                            disabled={simulating || !hasInputs}
                        >
                            {simulating ? "Simulating…" : "Run preview"}
                        </CollectPrimaryButton>
                        <CollectGhostButton
                            startIcon={<RestartAlt />}
                            onClick={handleReset}
                            disabled={simulating || saving}
                        >
                            Reset
                        </CollectGhostButton>
                    </Box>

                    {result && (
                        <>
                            <Divider sx={{ my: 2.5 }} />
                            <Typography
                                sx={{
                                    fontFamily: FONT,
                                    fontSize: "0.8rem",
                                    fontWeight: 700,
                                    color: colors.primary,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.08em",
                                    mb: 1.5,
                                }}
                            >
                                Scenario vs baseline
                            </Typography>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "1fr",
                                        sm: "repeat(3, 1fr)",
                                    },
                                    gap: 1.5,
                                }}
                            >
                                <DeltaCard
                                    label="On-time probability"
                                    baseline={pct(baselineOnTime)}
                                    scenario={pct(result.figures.onTimeProbability)}
                                    delta={result.onTimeProbabilityDelta}
                                    deltaFormat={(d) => `${d > 0 ? "+" : ""}${Math.round(d * 100)} pts`}
                                    positiveIsGood
                                />
                                <DeltaCard
                                    label="Projected cash impact"
                                    baseline={fmtCurrency(baselineCash, currency)}
                                    scenario={fmtCurrency(
                                        result.figures.projectedCashImpact,
                                        currency,
                                    )}
                                    delta={result.cashImpactDelta}
                                    deltaFormat={(d) =>
                                        `${d > 0 ? "+" : ""}${fmtCurrency(d, currency)}`
                                    }
                                    positiveIsGood
                                />
                                <Box
                                    sx={{
                                        backgroundColor: "#f9fafb",
                                        border: "1px solid rgba(15,23,42,0.06)",
                                        borderRadius: "12px",
                                        p: "14px 16px",
                                    }}
                                >
                                    <Typography sx={{ fontFamily: FONT, fontSize: "0.72rem", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>
                                        Risk level
                                    </Typography>
                                    <Chip
                                        size="small"
                                        label={
                                            PREDICTION_RISK_LEVEL_LABELS[
                                            result.figures.customerRiskLevel
                                            ]
                                        }
                                        sx={{
                                            mt: 0.5,
                                            fontWeight: 700,
                                            backgroundColor:
                                                RISK_COLORS[
                                                result.figures.customerRiskLevel
                                                ] + "1a",
                                            color: RISK_COLORS[
                                                result.figures.customerRiskLevel
                                            ],
                                        }}
                                    />
                                    <Typography sx={{ fontFamily: FONT, fontSize: "0.78rem", color: "#475569", mt: 1 }}>
                                        {
                                            RECOVERY_ACTION_LABELS[
                                            result.recommendedRecoveryAction
                                            ]
                                        }
                                    </Typography>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1.5,
                                    mt: 2.5,
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                }}
                            >
                                <TextField
                                    size="small"
                                    label="Scenario name (optional)"
                                    value={scenarioName}
                                    onChange={(e) => setScenarioName(e.target.value)}
                                    sx={{ minWidth: 240 }}
                                />
                                <CollectPrimaryButton
                                    startIcon={
                                        saving ? (
                                            <CircularProgress size={16} color="inherit" />
                                        ) : (
                                            <SaveOutlined />
                                        )
                                    }
                                    onClick={handleSave}
                                    disabled={saving || !hasInputs}
                                >
                                    {saving ? "Saving…" : "Save scenario"}
                                </CollectPrimaryButton>
                            </Box>
                        </>
                    )}
                </>
            )}
        </CollectSectionCard>
    );
};

// ── Delta stat card ──────────────────────────────────────────────────────
const DeltaCard: React.FC<{
    label: string;
    baseline: string;
    scenario: string;
    delta: number;
    deltaFormat: (d: number) => string;
    positiveIsGood?: boolean;
}> = ({ label, baseline, scenario, delta, deltaFormat, positiveIsGood }) => {
    const neutral = Math.abs(delta) < 1e-9;
    const good = positiveIsGood ? delta > 0 : delta < 0;
    const color = neutral ? "#6b7280" : good ? "#16a34a" : "#dc2626";
    return (
        <Box
            sx={{
                backgroundColor: "#f9fafb",
                border: "1px solid rgba(15,23,42,0.06)",
                borderRadius: "12px",
                p: "14px 16px",
            }}
        >
            <Typography sx={{ fontFamily: FONT, fontSize: "0.72rem", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>
                {label}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 0.5 }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: "1.2rem", color: colors.primary }}>
                    {scenario}
                </Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: "0.78rem", color: "#94a3b8", textDecoration: "line-through" }}>
                    {baseline}
                </Typography>
            </Box>
            <Typography sx={{ fontFamily: FONT, fontSize: "0.8rem", fontWeight: 700, color, mt: 0.25 }}>
                {neutral ? "No change" : deltaFormat(delta)}
            </Typography>
        </Box>
    );
};

export default WhatIfSimulator;
